/**
 * Quiz — multi-step questionnaire with product recommendation.
 *
 * Markup is rendered by sections/quiz.liquid; this file only drives it:
 *   intro → question 1..n → result
 *
 * State lives in one place (QuizManager#state: current step + answers keyed by question block id).
 * The recommendation engine lives in assets/quiz-recommendation.js (answers → profile → scores → restrictions →
 * ranking); its rules come from the section settings, so this file knows nothing about specific questions.
 */
(() => {
  class QuizManager {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-quiz-config]').textContent);
      this.engine = window.QuizRecommendation;
      this.recommendationRules = this.engine.readRules(this.config.rules);
      this.storageKey = `quiz:${location.pathname}`;

      this.steps = [...root.querySelectorAll('[data-quiz-step]')];
      this.progressBar = root.querySelector('[data-quiz-progress]');
      this.progressFill = root.querySelector('[data-quiz-progress-fill]');
      this.storeLink = root.querySelector('[data-quiz-store-link]');
      this.status = root.querySelector('[data-quiz-status]');
      this.emptyMessage = root.querySelector('[data-quiz-empty]');
      this.groups = [...root.querySelectorAll('[data-quiz-group]')].map((element) => ({
        element,
        list: element.querySelector('.splide__list'),
        slides: [...element.querySelectorAll('[data-quiz-slide]')],
        // Fallback candidates (a merchant-picked collection, unfiltered) live inert in a <template> until
        // the quiz filter leaves this group with zero products; see renderProducts().
        fallbackSlides: [...(element.querySelector('[data-quiz-fallback-slides]')?.content.querySelectorAll('[data-quiz-slide]') ?? [])],
        carousel: null
      }));

      this.state = { stepIndex: 0, answers: {} };
      this.restore();

      this.bindEvents();
      history.replaceState({ quizStep: this.state.stepIndex }, '');
      this.render({ focus: false });
    }

    get currentStep() {
      return this.steps[this.state.stepIndex];
    }

    get questionId() {
      return this.currentStep.dataset.questionId;
    }

    get view() {
      const type = this.currentStep.dataset.quizStep;
      return type === 'intro' || type === 'result' ? type : 'question';
    }

    get canAdvance() {
      return this.view !== 'question' || Boolean(this.state.answers[this.questionId]);
    }

    /** Furthest step the customer can be on given the answers so far (first unanswered question, or the result). */
    get lastReachableStep() {
      const firstUnanswered = this.steps.findIndex(
        (step) => step.dataset.questionId && !this.state.answers[step.dataset.questionId]
      );
      return firstUnanswered === -1 ? this.steps.length - 1 : firstUnanswered;
    }

    bindEvents() {
      this.root.addEventListener('change', (event) => {
        if (!event.target.matches('[data-quiz-option]')) return;
        this.state.answers[event.target.name] = event.target.value;
        this.save();
        this.renderNextButton();
      });

      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-quiz-next]')) this.next();
        else if (event.target.closest('[data-quiz-restart]')) this.goTo(1);
      });

      // There is no back button in the design: the browser/system back button walks the steps.
      // Moving between steps is not a page navigation, so other popstate listeners (third-party scripts) are not notified.
      window.addEventListener(
        'popstate',
        (event) => {
          const step = event.state?.quizStep;
          if (!Number.isInteger(step) || step < 0 || step >= this.steps.length) return;

          event.stopImmediatePropagation();
          this.goTo(Math.min(step, this.lastReachableStep), { history: false });
        },
        true
      );

      // Theme editor: show the step of the selected block.
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.steps.findIndex((step) => step.dataset.questionId === event.detail.blockId);
        if (index !== -1 && this.root.contains(event.target)) this.goTo(index, { focus: false, history: false });
      });
      document.addEventListener('shopify:section:unload', (event) => {
        if (this.root.contains(event.target)) this.destroyCarousels();
      });
    }

    next() {
      if (!this.canAdvance) {
        this.announceAnswerRequired();
        return;
      }
      this.goTo(Math.min(this.state.stepIndex + 1, this.steps.length - 1));
    }

    /** The button stays visually enabled (as in the design) but only advances once the question is answered. */
    announceAnswerRequired() {
      this.status.textContent = this.root.dataset.requiredMessage;
      this.currentStep.querySelector('[data-quiz-option]').focus();
    }

    goTo(index, { focus = true, history: pushHistory = true } = {}) {
      this.state.stepIndex = index;
      if (pushHistory && history.state?.quizStep !== index && !window.Shopify?.designMode) {
        history.pushState({ quizStep: index }, '');
      }
      this.save();
      this.render({ focus });
    }

    render({ focus }) {
      this.steps.forEach((step, index) => {
        step.hidden = index !== this.state.stepIndex;
      });

      this.root.dataset.view = this.view;
      this.storeLink.hidden = this.view !== 'result';

      this.syncSelectedOptions();
      this.renderNextButton();
      this.renderProgress();
      this.announceStep();

      if (this.view === 'result') this.renderResult();

      if (focus) {
        const heading = [...this.currentStep.querySelectorAll('[data-quiz-heading]')].find((el) => el.offsetParent);
        window.scrollTo({ top: 0 });
        heading?.focus({ preventScroll: true });
      }
    }

    /** Restores the chosen radio of the current question (kept when navigating back). */
    syncSelectedOptions() {
      if (this.view !== 'question') return;
      const answer = this.state.answers[this.questionId];
      this.currentStep.querySelectorAll('[data-quiz-option]').forEach((input) => {
        input.checked = input.value === answer;
      });
    }

    renderNextButton() {
      const button = this.currentStep.querySelector('[data-quiz-next]');
      if (button) button.setAttribute('aria-disabled', String(!this.canAdvance));
    }

    renderProgress() {
      const { progressBase, introProgress, resultProgress, questions } = this.config;
      let pixels = introProgress;
      if (this.view === 'result') pixels = resultProgress;
      else if (this.view === 'question') pixels = questions.find((question) => question.id === this.questionId).progress;

      const percent = (pixels / progressBase) * 100;
      this.progressFill.style.width = `${percent}%`;
      this.progressBar.setAttribute('aria-valuenow', Math.round(percent));
    }

    announceStep() {
      this.status.textContent = this.root.dataset.stepStatus
        .replace('__current__', this.state.stepIndex + 1)
        .replace('__total__', this.steps.length);
    }

    renderResult() {
      this.root.querySelectorAll('[data-quiz-summary-row]').forEach((row) => {
        row.querySelector('[data-quiz-summary-answer]').textContent = this.state.answers[row.dataset.quizSummaryRow] || '';
      });
      this.renderProducts();
    }

    renderProducts() {
      const { tagPrefix, showUnscored, useFallbackWhenEmpty } = this.config.recommendation;
      let total = 0;

      this.destroyCarousels();
      this.groups.forEach((group) => {
        const candidates = group.slides.map((slide) => {
          const el = slide.querySelector('[data-quiz-product]');
          return { slide, id: el.dataset.productHandle, tags: el.dataset.productTags.split('|'), warningText: el.dataset.productWarning };
        });
        // `evaluated` (score/profiles per candidate) is available for debugging via
        // window.QuizRecommendation.recommend(...) in the console; nothing is written to the DOM.
        const { products } = this.engine.recommend({
          products: candidates,
          answers: this.state.answers,
          rules: this.recommendationRules,
          tagPrefix,
          showUnscored,
          max: group.element.dataset.maxProducts
        });

        // Nothing survived the quiz filter: fall back to the merchant-picked collection for this group
        // (unfiltered), when that's configured and turned on — otherwise the group stays hidden and the
        // empty-state message (below) covers it.
        const slides = products.length ? products.map(({ slide }) => slide)
          : useFallbackWhenEmpty && group.fallbackSlides.length ? group.fallbackSlides.map((slide) => slide.cloneNode(true))
          : [];

        group.list.replaceChildren(...slides);
        group.element.hidden = slides.length === 0;
        if (slides.length) group.carousel = this.mountCarousel(group.element);
        total += slides.length;
      });

      this.emptyMessage.classList.toggle('hidden', total > 0);
    }

    mountCarousel(element) {
      if (typeof Splide === 'undefined') return null;

      return new Splide(element, {
        type: 'loop',
        perPage: 4,
        perMove: 1,
        gap: '2.4rem',
        arrows: true,
        pagination: false,
        i18n: { prev: element.dataset.labelPrev, next: element.dataset.labelNext },
        breakpoints: {
          989: { fixedWidth: '22.8rem', perPage: 1, gap: '1.2rem', arrows: false, padding: 0 }
        }
      }).mount();
    }

    destroyCarousels() {
      this.groups.forEach((group) => {
        group.carousel?.destroy();
        group.carousel = null;
      });
    }

    /**
     * Keeps the answers across a reload / back-forward navigation (session only). A fresh visit starts over.
     * Anything that no longer matches the markup is dropped.
     */
    restore() {
      try {
        if (performance.getEntriesByType('navigation')[0]?.type === 'navigate') {
          sessionStorage.removeItem(this.storageKey);
          return;
        }

        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (!saved) return;

        this.steps.forEach((step) => {
          const id = step.dataset.questionId;
          const options = [...step.querySelectorAll('[data-quiz-option]')].map((input) => input.value);
          if (id && options.includes(saved.answers?.[id])) this.state.answers[id] = saved.answers[id];
        });
        this.state.stepIndex = Math.min(Number(saved.stepIndex) || 0, this.lastReachableStep);
      } catch {
        this.state = { stepIndex: 0, answers: {} };
      }
    }

    save() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch {
        /* storage unavailable: the quiz simply restarts on reload */
      }
    }
  }

  document.querySelectorAll('[data-quiz]:not([data-quiz-ready])').forEach((root) => {
    root.dataset.quizReady = '';
    new QuizManager(root);
  });
})();
